import "./index.css";

import { Button } from "@tql/ui-components-react/tql/buttons";
import { useCallback, useMemo, useState } from "react";

import { routes } from "~/app/routes/web-routes.ts";
import { convertToGridColumnProps } from "~/features/quote-board/utils/quote-board-column-processor.ts";
import { TraxGrid } from "~/shared/components/trax-grid/trax-grid.tsx";
import { TraxPageHeader } from "~/shared/components/trax-page-header/trax-page-header.tsx";
import { TraxPageLayout } from "~/shared/components/trax-page-layout/trax-page-layout.tsx";
import { useCustomerSelection } from "~/shared/hooks/use-customer-selection.ts";
import { useDeploymentType } from "~/shared/hooks/use-deployment-type.ts";
import { useGridState } from "~/shared/hooks/use-grid-state.ts";
import { useIsMobile } from "~/shared/hooks/use-is-mobile.ts";
import { ConfigurationType } from "~/shared/schemas/config-schema.ts";
import { DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS } from "~/shared/utils/default-grid-pager-settings.ts";
import { getDefaultTemplatesGridPrefs } from "~/shared/utils/default-grid-prefs.ts";
import { GridIdentifiers } from "~/shared/utils/grid-identifier-enums.ts";

import { useGetInternalUserPreferences } from "../profile/hooks/internal/use-get-internal-user-preferences.ts";
import { useInternalGridPreferencesSync } from "../profile/hooks/internal/use-internal-grid-preferences-sync.ts";
import { useGetUserPreferences } from "../profile/hooks/use-get-user-preferences.ts";
import { useGridPreferencesSync } from "../profile/hooks/use-grid-preferences-sync.ts";
import { AddEditTemplateModal } from "./components/add-edit-template-modal/add-edit-template-modal.tsx";
import { TemplatesHeader } from "./components/templates-header/templates-header.tsx";
import { TemplatesMobile } from "./components/templates-mobile/templates-mobile.tsx";
import { useTemplateColumns } from "./hooks/use-template-columns.tsx";
import { useTemplateGridColumns } from "./hooks/use-template-grid-columns.ts";
import {
	useDeleteTemplate,
	useDuplicateTemplate,
	useTemplateDefault,
	useTemplates,
} from "./hooks/use-templates.ts";

const onSave = () => {
	console.log("Save");
};

export const Templates = () => {
	const { isInternal } = useDeploymentType();
	const isMobile = useIsMobile();
	const { activeCustomer, handleSelectCustomer } = useCustomerSelection({
		baseRoute: routes.templates,
		customerRoute: routes.templatesWithCustomer,
	});

	const [selectedTemplateItem, setSelectedTemplateItem] = useState<
		number | undefined
	>(undefined);

	const [showAddNewTemplateModal, setShowAddNewTemplateModal] = useState(false);

	const templatesQuery = useTemplates(activeCustomer?.id);
	const templates = templatesQuery.data;
	const isTemplatesLoading = templatesQuery.isLoading;

	const { skip, take, handlePageChange, isLoadingPreference } = useGridState(
		GridIdentifiers.Templates,
	);

	const { duplicateTemplate, isDuplicating } = useDuplicateTemplate(templates);

	// The mobile card view drives default/delete actions directly; these share
	// the same TanStack Query cache as the grid columns' hooks. Their pending
	// state feeds the page loading state and disables the card controls.
	const {
		toggleDefault,
		userConfigs,
		isHandlingDefault: isMobileHandlingDefault,
	} = useTemplateDefault(activeCustomer?.id);
	const { deleteTemplate, isDeleting: isMobileDeleting } = useDeleteTemplate();
	const isMobileMutating = isMobileDeleting || isMobileHandlingDefault;

	const isDefaultTemplate = useCallback(
		(templateId: number) =>
			userConfigs.some(
				(c) =>
					c.configType === ConfigurationType.QuoteTemplateDefault &&
					Number(c.customerId) === Number(activeCustomer?.id) &&
					c.configValue === String(templateId),
			),
		[userConfigs, activeCustomer?.id],
	);

	const handleToggleDefault = useCallback(
		(templateId: number, shouldBeDefault: boolean) => {
			void toggleDefault(templateId, shouldBeDefault);
		},
		[toggleDefault],
	);

	const handleCopyTemplate = useCallback(
		async (templateId: number) => {
			const newTemplateId = await duplicateTemplate(templateId);
			if (newTemplateId) {
				setSelectedTemplateItem(newTemplateId);
			}
		},
		[duplicateTemplate],
	);
	const {
		templateColumns,
		isDeleting,
		isHandlingDefault,
		DeleteConfirmationModal,
		CopyConfirmationModal,
	} = useTemplateColumns({
		customerId: activeCustomer?.id,
		isLoading: isTemplatesLoading || isDuplicating,
		setSelectedTemplateItem,
		onCopyTemplate: handleCopyTemplate,
	});

	// Internal and external users both use the server-backed preference path
	// (visibility, order, width) via independent endpoints/hooks. Both data and
	// sync hooks are called unconditionally; each self-disables its network
	// request for the other deployment, and the active set is selected by
	// deployment type. The external flow is left fully untouched.
	const { data: externalUserPreferences } = useGetUserPreferences(
		GridIdentifiers.Templates,
	);
	const { data: internalUserPreferences } = useGetInternalUserPreferences(
		GridIdentifiers.Templates,
	);
	const userPreferences = isInternal
		? internalUserPreferences
		: externalUserPreferences;

	const selectedColumns = useTemplateGridColumns({
		templateColumns,
		isInternal,
		columnPrefs: userPreferences?.columns,
	});

	const gridPrefsOptions = {
		defaultPrefs:
			getDefaultTemplatesGridPrefs(isInternal)[GridIdentifiers.Templates]
				?.columns,
	};
	const externalGridPrefs = useGridPreferencesSync(
		GridIdentifiers.Templates,
		gridPrefsOptions,
	);
	const internalGridPrefs = useInternalGridPreferencesSync(
		GridIdentifiers.Templates,
		gridPrefsOptions,
	);
	const {
		gridKey: prefsGridKey,
		onColumnVisibilityChange: handleColumnToggle,
		onColumnReorder: handleColumnReorder,
		onColumnResize: handleColumnResize,
		resetColumns: handleResetColumns,
	} = isInternal ? internalGridPrefs : externalGridPrefs;

	const onCancel = useCallback(() => {
		setSelectedTemplateItem(undefined);
	}, []);

	const processedColumns = useMemo(
		() => selectedColumns.filter((col) => col.show),
		[selectedColumns],
	);

	// Both deployments render at their persisted/canonical widths; internal
	// width auto-distribution was removed so saved widths are honored.
	const gridColumns = processedColumns;

	const toggleAddNewTemplateModal = () => {
		setShowAddNewTemplateModal(!showAddNewTemplateModal);
	};

	const selectedTemplate = useMemo(() => {
		if (!selectedTemplateItem || !templates) return undefined;
		return templates.find((t) => t.templateId === selectedTemplateItem);
	}, [selectedTemplateItem, templates]);

	const handleEditClose = useCallback(() => {
		setSelectedTemplateItem(undefined);
	}, []);

	const isAnyLoading =
		isTemplatesLoading ||
		isDuplicating ||
		isDeleting ||
		isHandlingDefault ||
		isMobileMutating;

	const mobileView = (
		<TemplatesMobile
			customerId={activeCustomer?.id ?? 0}
			customerName={activeCustomer?.name ?? ""}
			deleteTemplate={deleteTemplate}
			isBusy={isMobileMutating}
			isDefaultTemplate={isDefaultTemplate}
			isInternal={isInternal}
			isLoading={isTemplatesLoading || isLoadingPreference}
			onClearCustomer={() => handleSelectCustomer(null)}
			onEdit={setSelectedTemplateItem}
			onRefresh={() => void templatesQuery.refetch()}
			templates={templates}
			toggleDefault={handleToggleDefault}
		/>
	);

	const gridView = (
		<div className={"templates-grid-wrapper"}>
			<TraxGrid
				key={`templates-grid-${prefsGridKey}`}
				className={"templates-grid"}
				columns={convertToGridColumnProps(gridColumns)}
				data={templates}
				dataItemKey={"templateId"}
				filterable={true}
				loading={isAnyLoading || isLoadingPreference}
				loadingRows={10}
				onColumnReorder={handleColumnReorder}
				onColumnResize={handleColumnResize}
				onPageChange={handlePageChange}
				pageable={DEFAULT_EXTERNAL_GRID_PAGER_SETTINGS}
				reorderable={true}
				resizable={true}
				skip={skip}
				take={take}
			/>
			<div className={"templates-actions"}>
				<div>
					<Button onClick={toggleAddNewTemplateModal} variant={"primary"}>
						Add New Template
					</Button>
				</div>
			</div>
		</div>
	);

	const activeCustomerContent = isMobile ? mobileView : gridView;

	return (
		<TraxPageLayout>
			{showAddNewTemplateModal ? (
				<AddEditTemplateModal toggleModal={toggleAddNewTemplateModal} />
			) : null}
			{selectedTemplateItem && selectedTemplate ? (
				<AddEditTemplateModal
					template={selectedTemplate}
					toggleModal={handleEditClose}
				/>
			) : null}
			<DeleteConfirmationModal />
			<CopyConfirmationModal />
			<TraxPageHeader
				isLtlOnly={!isInternal}
				representativeType={isInternal ? undefined : "ltl"}
				showSupportMessage={!isInternal}
				title={"Templates"}
			/>
			<div className={"templates-page-content"}>
				{isMobile && activeCustomer ? null : (
					<TemplatesHeader
						columns={selectedColumns}
						isLoading={isAnyLoading}
						isMobile={isMobile}
						onCancel={onCancel}
						onColumnToggle={handleColumnToggle}
						onCustomerChange={handleSelectCustomer}
						onResetColumns={handleResetColumns}
						onSave={onSave}
						selectedCustomer={activeCustomer}
						selectedTemplateItem={selectedTemplateItem}
						setSelectedTemplateItem={setSelectedTemplateItem}
					/>
				)}

				{activeCustomer ? activeCustomerContent : null}

				{activeCustomer && isMobile ? (
					<div className={"templates-actions"}>
						<div>
							<Button onClick={toggleAddNewTemplateModal} variant={"primary"}>
								Add New Template
							</Button>
						</div>
					</div>
				) : null}
			</div>
		</TraxPageLayout>
	);
};
